// src/components/DeepFikaRespond.jsx
// Landing page for Deep Fika match invitation accept/decline links
// URL: /deep-fika/respond?session=...&participant=...&token=...&action=accept|decline

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

async function callAdminAPI(action, data) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/curated-fika-admin`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${ANON_KEY}`,
    },
    body: JSON.stringify({ action, data }),
  });
  return res.json();
}

const DeepFikaRespond = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const sessionId = searchParams.get('session');
  const applicationId = searchParams.get('participant');
  const token = searchParams.get('token');
  const action = searchParams.get('action'); // 'accept' or 'decline'

  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Auto-submit if required params present
  useEffect(() => {
    if (sessionId && applicationId && (action === 'accept' || action === 'decline')) {
      handleResponse();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResponse = async () => {
    if (!sessionId || !applicationId || !action) {
      setStatus('error');
      setErrorMessage('This link is missing required information. Please check your email for the correct link.');
      return;
    }

    setStatus('loading');

    try {
      const res = await callAdminAPI('handle_deep_fika_response', {
        sessionId,
        applicationId,
        token,
        response: action,
      });

      if (res.success) {
        setResult(res.data);
        setStatus('success');
      } else {
        throw new Error(res.error || 'Something went wrong');
      }
    } catch (err) {
      setStatus('error');
      setErrorMessage(err.message || 'An unexpected error occurred.');
    }
  };

  const isAccepting = action === 'accept';

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-emerald-50 to-stone-100 flex items-center justify-center px-4 py-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-3xl shadow-xl max-w-md w-full overflow-hidden"
      >
        {/* Logo strip */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-8 py-5 text-center">
          <div className="text-white text-2xl font-bold tracking-tight">Connect & Co</div>
          <div className="text-emerald-100 text-sm mt-0.5">Deep Fika — 1-on-1</div>
        </div>

        <div className="px-8 py-8 text-center">
          {/* Loading */}
          {status === 'loading' && (
            <div>
              <div className="text-4xl mb-4">🫂</div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Processing your response…</h2>
              <p className="text-gray-500 text-sm">Just a moment.</p>
              <div className="mt-6 flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
              </div>
            </div>
          )}

          {/* Idle (missing params — shouldn't normally show) */}
          {status === 'idle' && (
            <div>
              <div className="text-4xl mb-4">🔗</div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Invalid link</h2>
              <p className="text-gray-500 text-sm">
                This link doesn't look right. Please use the Accept or Decline button directly from your email.
              </p>
              <button
                onClick={() => navigate('/')}
                className="mt-6 px-5 py-2.5 bg-emerald-600 text-white rounded-full text-sm font-semibold hover:bg-emerald-700 transition-colors"
              >
                Go to ConnectCo
              </button>
            </div>
          )}

          {/* Success — accepted, waiting for other person */}
          {status === 'success' && result?.response === 'accepted' && !result?.mutual && !result?.alreadyResponded && (
            <div>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
                className="text-5xl mb-4"
              >
                ✅
              </motion.div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                You're in!
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                You've accepted the match. We're waiting to hear back from your potential match.
                When they also accept, we'll send both of you each other's contact info so you can
                coordinate directly.
              </p>
              <div className="mt-5 p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-left">
                <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-2">What happens next</div>
                <ul className="text-xs text-emerald-800 space-y-1.5">
                  <li>⏳ Your match has 7 days to respond</li>
                  <li>📱 If they accept, you'll both get contact info by email</li>
                  <li>☕ You coordinate venue and time at your convenience</li>
                </ul>
              </div>
            </div>
          )}

          {/* Success — both accepted, contact info shared */}
          {status === 'success' && result?.response === 'accepted' && result?.mutual && (
            <div>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
                className="text-5xl mb-4"
              >
                🎉
              </motion.div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                It's a match!
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                You've both accepted. We just sent each of you an email with the other person's
                contact information. Reach out, find a cafe you both like, and enjoy your Deep Fika.
              </p>
              <div className="mt-5 p-4 bg-amber-50 border border-amber-200 rounded-xl text-left">
                <div className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1">Check your inbox</div>
                <p className="text-xs text-amber-800">
                  We've sent a contact-sharing email to both of you. Don't see it? Check your spam folder.
                </p>
              </div>
            </div>
          )}

          {/* Success — declined */}
          {status === 'success' && result?.response === 'declined' && (
            <div>
              <div className="text-4xl mb-4">🙏</div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                No problem at all.
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                You've declined this match. Your application stays active and we may suggest a different
                match in the future. Thank you for being honest — it helps us make better matches.
              </p>
              <button
                onClick={() => navigate('/')}
                className="mt-6 px-5 py-2.5 bg-stone-800 text-white rounded-full text-sm font-semibold hover:bg-stone-900 transition-colors"
              >
                Back to ConnectCo
              </button>
            </div>
          )}

          {/* Success — already responded */}
          {status === 'success' && result?.alreadyResponded && (
            <div>
              <div className="text-4xl mb-4">👋</div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                You've already responded.
              </h2>
              <p className="text-gray-600 text-sm">
                Looks like you already {isAccepting ? 'accepted' : 'declined'} this match invitation. No action needed.
              </p>
              <button
                onClick={() => navigate('/')}
                className="mt-6 px-5 py-2.5 bg-emerald-600 text-white rounded-full text-sm font-semibold hover:bg-emerald-700 transition-colors"
              >
                Back to ConnectCo
              </button>
            </div>
          )}

          {/* Error */}
          {status === 'error' && (
            <div>
              <div className="text-4xl mb-4">⚠️</div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h2>
              <p className="text-gray-600 text-sm mb-1">{errorMessage}</p>
              <p className="text-gray-400 text-xs">
                If this keeps happening, email us at{' '}
                <a href="mailto:mark@connectco.app" className="underline">mark@connectco.app</a>
                {' '}and we'll sort it out.
              </p>
              <button
                onClick={() => navigate('/')}
                className="mt-6 px-5 py-2.5 bg-stone-800 text-white rounded-full text-sm font-semibold hover:bg-stone-900 transition-colors"
              >
                Back to ConnectCo
              </button>
            </div>
          )}
        </div>

        <div className="px-8 pb-6 text-center">
          <p className="text-xs text-gray-400">
            ConnectCo · Tokyo's fika community · connectco.app
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default DeepFikaRespond;
